import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createMenu,
  getMenus,
  getMenusNested,
  getMenuById,
  updateMenu,
  deleteMenu,
  deleteMenuCascade,
} from '@app/api/menus.api';
import { Menu, CreateMenuDto, UpdateMenuDto } from '@app/types/api.types';

export interface MenuState {
  items: Menu[];
  nestedItems: Menu[];
  currentItem: Menu | null;
  loading: boolean;
  error: string | null;
}

const initialState: MenuState = {
  items: [],
  nestedItems: [],
  currentItem: null,
  loading: false,
  error: null,
};

export const fetchMenus = createAsyncThunk(
  'menu/fetchMenus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getMenus();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch menus');
    }
  }
);

export const fetchMenusNested = createAsyncThunk(
  'menu/fetchMenusNested',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getMenusNested();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch nested menus');
    }
  }
);

export const fetchMenuById = createAsyncThunk(
  'menu/fetchMenuById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await getMenuById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch menu');
    }
  }
);

export const createMenuAction = createAsyncThunk(
  'menu/createMenu',
  async (data: CreateMenuDto, { rejectWithValue }) => {
    try {
      const response = await createMenu(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create menu');
    }
  }
);

export const updateMenuAction = createAsyncThunk(
  'menu/updateMenu',
  async ({ id, data }: { id: string; data: UpdateMenuDto }, { rejectWithValue }) => {
    try {
      const response = await updateMenu(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update menu');
    }
  }
);

export const deleteMenuAction = createAsyncThunk(
  'menu/deleteMenu',
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteMenu(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete menu');
    }
  }
);

export const deleteMenuCascadeAction = createAsyncThunk(
  'menu/deleteMenuCascade',
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteMenuCascade(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete menu with cascade');
    }
  }
);

const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Menus
    builder.addCase(fetchMenus.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMenus.fulfilled, (state, action) => {
      state.loading = false;
      state.items = action.payload;
    });
    builder.addCase(fetchMenus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Menus Nested
    builder.addCase(fetchMenusNested.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMenusNested.fulfilled, (state, action) => {
      state.loading = false;
      state.nestedItems = action.payload;
    });
    builder.addCase(fetchMenusNested.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Menu By ID
    builder.addCase(fetchMenuById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMenuById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentItem = action.payload;
    });
    builder.addCase(fetchMenuById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create Menu
    builder.addCase(createMenuAction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createMenuAction.fulfilled, (state, action) => {
      state.loading = false;
      state.items.push(action.payload);
    });
    builder.addCase(createMenuAction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update Menu
    builder.addCase(updateMenuAction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateMenuAction.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.items.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      if (state.currentItem?.id === action.payload.id) {
        state.currentItem = action.payload;
      }
    });
    builder.addCase(updateMenuAction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Delete Menu
    builder.addCase(deleteMenuAction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteMenuAction.fulfilled, (state, action) => {
      state.loading = false;
      state.items = state.items.filter((item) => item.id !== action.payload);
      if (state.currentItem?.id === action.payload) {
        state.currentItem = null;
      }
    });
    builder.addCase(deleteMenuAction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Delete Menu Cascade
    builder.addCase(deleteMenuCascadeAction.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteMenuCascadeAction.fulfilled, (state, action) => {
      state.loading = false;
      // Remove the menu and all its children recursively
      const removeMenuAndChildren = (items: Menu[], id: string): Menu[] => {
        return items.filter((item) => {
          if (item.id === id) {
            return false;
          }
          if (item.children) {
            item.children = removeMenuAndChildren(item.children, id);
          }
          return true;
        });
      };
      state.items = removeMenuAndChildren(state.items, action.payload);
      state.nestedItems = removeMenuAndChildren(state.nestedItems, action.payload);
      if (state.currentItem?.id === action.payload) {
        state.currentItem = null;
      }
    });
    builder.addCase(deleteMenuCascadeAction.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearCurrentItem } = menuSlice.actions;
export default menuSlice.reducer;

